import {Async, AsyncPage, Empty} from 'Core/models/Async';
import {DateRange, EmptyDateRange} from 'Core/models/DateRange';
import {Page} from 'Core/models/Page';
import {EnrollmentFileNotes} from 'Enrollment/models/EnrollmentFileNotes';
import {EnrollmentFileReject} from 'Enrollment/models/EnrollmentFileReject';
import {EnrollmentFileRejectDetail} from 'Enrollment/models/EnrollmentFileRejectDetail';
import {OverallEnrollmentMetricsPayload} from 'Enrollment/models/EnrollmentMetricsPayload';
import {EnrollmentFileRejectsGetEmail} from 'Enrollment/models/EnrollmentFileRejectsGetEmail';
import {EnrollmentFileRejectsSendEmail} from 'Enrollment/models/EnrollmentFileRejectsSendEmail';
import * as O from 'fp-ts/lib/Option';
import {createAction} from 'Shared/utilities/actionCreator';
import {FilterOptionUnion} from './../../Core/models/FilterOption';
import {InequalityRangeFilter} from 'Core/models/InequalityRange';
import {Sort} from 'Shared/components/PagedView/Table/Sort';
import {PageFetchAction} from 'Shared/utilities/sagaApiGenerators';
import {constant} from 'fp-ts/lib/function';
import {pipe} from 'fp-ts/lib/pipeable';
import {EnrollmentFileRejectApplication} from 'Enrollment/models/EnrollmentFileRejectApplication';
import {EnrollmentFileRejectEmailHistory} from 'Enrollment/models/EnrollmentFileRejectEmailHistory';
import {GetRejectEmailRequestDto} from 'Enrollment/models/GetRejectEmailRequest';

export enum EnrollmentFileRejectActionType {
  GET_ENROLLMENT_FILE_REJECT = 'enrollmentFile/GET_ENROLLMENT_FILE_REJECT',
  GET_ENROLLMENT_FILE_REJECT_BY_CASE_REQUEST_ID = 'enrollmentFile/GET_ENROLLMENT_FILE_REJECT_BY_CASE_REQUEST_ID',
  GET_ENROLLMENT_FILE_REJECT_DETAILS = 'enrollmentFile/GET_ENROLLMENT_FILE_REJECT_DETAILS',
  GET_ENROLLMENT_FILE_REJECT_DETAIL_APPLICATIONS = 'enrollmentFile/GET_ENROLLMENT_FILE_REJECT_DETAIL_APPLICATIONS',
  GET_ENROLLMENT_FILE_REJECTS = 'enrollmentFile/GET_ENROLLMENT_FILES_REJECTS',
  GET_ENROLLMENT_FILE_REJECT_NOTES = 'enrollmentFile/GET_ENROLLMENT_FILE_REJECT_NOTES',
  CHANGE_ENROLLMENT_FILE_REJECTS = 'enrollmentFile/CHANGE_ENROLLMENT_FILE_REJECTS',
  CHANGE_ENROLLMENT_FILE_REJECT_DETAIL = 'enrollmentFile/CHANGE_ENROLLMENT_FILE_REJECT_DETAIL',
  CHANGE_ENROLLMENT_FILE_REJECT_DETAIL_APPLICATIONS = 'enrollmentFile/CHANGE_ENROLLMENT_FILE_REJECT_DETAIL_APPLICATIONS',
  CHANGE_ENROLLMENT_FILE_REJECT_SORT = 'enrollmentFile/CHANGE_ENROLLMENT_FILE_REJECT_SORT',
  CLEAR_ENROLLMENT_FILE_REJECT_SORT = 'enrollmentFile/CLEAR_ENROLLMENT_FILE_REJECT_SORT',
  CHANGE_ENROLLMENT_FILE_REJECT_FILTERS = 'enrollmentFile/CHANGE_ENROLLMENT_FILE_REJECT_FILTERS',
  CHANGE_ENROLLMENT_FILE_REJECT_DETAILS_FILTERS = 'enrollmentFile/CHANGE_ENROLLMENT_FILE_REJECT_DETAILS_FILTERS',
  CHANGE_ENROLLMENT_FILE_NOTES = 'enrollmentFile/CHANGE_ENROLLMENT_FILE_NOTES',
  CLEAR_ENROLLMENT_FILE__REJECTS = 'enrollmentFile/CLEAR_ENROLLMENT_FILE__REJECTS',
  CHANGE_ENROLLMENT_METRICS = 'enrollmentFileProcessing/CHANGE_ENROLLMENT_METRICS',
  GET_ENROLLMENT_METRICS = 'enrollmentFileProcessing/GET_ENROLLMENT_METRICS',
  GET_ENROLLMENT_FILE_REJECTS_EMAIL = 'enrollmentFile/GET_ENROLLMENT_FILE_REJECTS_EMAIL',
  CHANGE_ENROLLMENT_FILE_REJECTS_EMAIL = 'enrollmentFile/CHANGE_ENROLLMENT_FILE_REJECTS_EMAIL',
  SEND_ENROLLMENT_FILE_REJECTS_EMAIL = 'enrollmentFile/SEND_ENROLLMENT_FILE_REJECTS_EMAIL',
  CHANGE_ENROLLMENT_FILE_REJECT_FILE_PROFILE = 'enrollmentFile/CHANGE_ENROLLMENT_FILE_REJECT_FILE_PROFILE',
  CHANGE_ENROLLMENT_FILE_REJECT_RECORDS = 'enrollmentFile/CHANGE_ENROLLMENT_FILE_REJECT_RECORDS',
  GET_ENROLLMENT_FILE_REJECTS_EMAIL_HISTORY = 'enrollmentFile/GET_ENROLLMENT_FILE_REJECTS_EMAIL_HISTORY',
  CHANGE_ENROLLMENT_FILE_REJECTS_EMAIL_HISTORY = 'enrollmentFile/CHANGE_ENROLLMENT_FILE_REJECTS_EMAIL_HISTORY',
  CHANGE_ENROLLMENT_FILE_REJECTS_EMAIL_HISTORY_FILTERS = 'enrollmentFile/CHANGE_ENROLLMENT_FILE_REJECTS_EMAIL_HISTORY_FILTERS',
  CLEAR_ENROLLMENT_FILE__REJECT_DETAILS = 'enrollmentFile/CLEAR_ENROLLMENT_FILE__REJECT_DETAILS',
  AUDIT_ENROLLMENT_FILE_REJECTS_EMAIL_ATTACHMENT_DOWNLOAD = 'enrollmentFile/AUDIT_ENROLLMENT_FILE_REJECTS_EMAIL_ATTACHMENT_DOWNLOAD',
}

export type FilterEnum = {
  name: string;
  value: string;
};

export const EmptyEnrollmentFileRejectFilter: EnrollmentFileRejectFilters = {
  fileName: '',
  checkSum: '',
  accountNumber: '',
  technologyPartner: '',
  evaluationDate: EmptyDateRange,
  mostRecentSentDate: EmptyDateRange,
  deleteDate: EmptyDateRange,
  caseRequestID: '',
  totalApps: O.none,
  enrollTrackRejectApps: O.none,
  applicationSource: [],
  emailStatus: [{name: 'Unsent', value: 'Unsent', tag: 'SelectedOption'}],
  rejectedBy: '',
};

export type EnrollmentFileRejectFilters = {
  pageSize?: number;
  fileName: string;
  checkSum: string;
  accountNumber: string;
  technologyPartner: string;
  evaluationDate: DateRange;
  mostRecentSentDate: DateRange;
  deleteDate: DateRange;
  caseRequestID: string;
  totalApps: InequalityRangeFilter;
  enrollTrackRejectApps: InequalityRangeFilter;
  applicationSource: FilterOptionUnion[],
  emailStatus: FilterOptionUnion[];
  rejectedBy: string;
};

export const EmptyEnrollmentFileRejectDetailsFilter: EnrollmentFileRejectDetailsFilters = {
  caseRequestId: '',
  firstName: '',
  lastName: '',
  accountNumber: '',
  product: '',
  requestType: '',
  errorReason: '',
  rejectedBy: '',
  ssn: '',
  employerName: '',
};

export type EnrollmentFileRejectDetailsFilters = {
  caseRequestId: string;
  firstName: string;
  lastName: string;
  accountNumber: string;
  product: string;
  requestType: string;
  errorReason: string;
  rejectedBy: string;
  ssn: string;
  employerName: string;
};

export type EnrollmentFileRejectEmailHistoryFilters = {
  caseRequestId: string;
  sentDate: DateRange;
  to: string;
  cc: string;
  from: string;
  subject: string;
  body: string;
  sentBy: string;
};

export const EmptyEnrollmentFileRejectEmailHistoryFilter: EnrollmentFileRejectEmailHistoryFilters = {
  caseRequestId: '',
  sentDate: EmptyDateRange,
  to: '',
  cc: '',
  from: '',
  subject: '',
  body: '',
  sentBy: '',
};

export const getEnrollmentMetrics = () => createAction(EnrollmentFileRejectActionType.GET_ENROLLMENT_METRICS);
export const changeEnrollmentMetrics = (enrollmentMetrics: Async<OverallEnrollmentMetricsPayload>) =>
  createAction(EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_METRICS, enrollmentMetrics);

export const getEnrollmentFileRejectByCaseRequestId = (id: string, applicationSource: string) =>
  createAction(EnrollmentFileRejectActionType.GET_ENROLLMENT_FILE_REJECT_BY_CASE_REQUEST_ID, {id, applicationSource});
export const changeEnrollmentFileRejectApplication = (applications: AsyncPage<Page<EnrollmentFileRejectApplication>>) =>
  createAction(EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECT_DETAIL_APPLICATIONS, applications);
export const changeEnrollmentFileRejectDetail = (enrollmentFileRejectDetail: Async<EnrollmentFileRejectDetail>) =>
  createAction(EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECT_DETAIL, enrollmentFileRejectDetail);
export const changeEnrollmentFileRejectFilters = (filters: EnrollmentFileRejectFilters) =>
  createAction(EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECT_FILTERS, {
    filters,
  });
export const changeEnrollmentFileRejectDetailsFilters = (filters: EnrollmentFileRejectDetailsFilters) =>
  createAction(EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECT_DETAILS_FILTERS, {
    filters,
  });
export const clearEnrollmentFileRejects = () =>
  createAction(EnrollmentFileRejectActionType.CLEAR_ENROLLMENT_FILE__REJECTS, DEFAULT_STATE.enrollmentFileRejects);

export const clearEnrollmentFileRejectDetails = () =>
  createAction(
    EnrollmentFileRejectActionType.CLEAR_ENROLLMENT_FILE__REJECT_DETAILS,
    DEFAULT_STATE.enrollmentFileRejectApplications
  );

export const getEnrollmentFileRejects = (
  pageOption?: Page<EnrollmentFileReject>,
  filter?: EnrollmentFileRejectFilters,
  sort?: Sort
) => {
  return createAction(EnrollmentFileRejectActionType.GET_ENROLLMENT_FILE_REJECTS, {pageOption, filter, sort});
};

export const getEnrollmentFileRejectDetailApplications = (
  id?: string,
  applicationSource?: string,
  pageOption?: Page<EnrollmentFileRejectApplication>,
  filter?: EnrollmentFileRejectDetailsFilters,
  sort?: Sort
) => {
  const payload = pipe(
    O.fromNullable(pageOption),
    O.fold(constant(PageFetchAction.Initial({filter})), page =>
      PageFetchAction.Additional({existingPage: page, filter})
    )
  );
  return createAction(
    EnrollmentFileRejectActionType.GET_ENROLLMENT_FILE_REJECT_DETAIL_APPLICATIONS,
    payload,
    id!,
    applicationSource!,
    sort!
  );
};

export const changeEnrollmentFileRejects = (enrollmentFileRejects: AsyncPage<Page<EnrollmentFileReject>>) =>
  createAction(EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECTS, enrollmentFileRejects);

export const getEnrollmentFileNotes = (fileName: string) =>
  createAction(EnrollmentFileRejectActionType.GET_ENROLLMENT_FILE_REJECT_NOTES, {fileName});

export const changeEnrollmentFileRejectNotes = (notes: EnrollmentFileNotes[]) =>
  createAction(EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_NOTES, notes);

export const getEnrollmentFileRejectsEmail = (rejectFile: GetRejectEmailRequestDto) =>
  createAction(EnrollmentFileRejectActionType.GET_ENROLLMENT_FILE_REJECTS_EMAIL, {
    rejectFile,
  });

export const changeEnrollmentFileRejectsEmail = (email: Async<EnrollmentFileRejectsGetEmail>) =>
  createAction(EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECTS_EMAIL, email);

export const sendEnrollmentFileRejectsEmail = (email: EnrollmentFileRejectsSendEmail, applicationSource: string, onSuccess: () => void) =>
  createAction(EnrollmentFileRejectActionType.SEND_ENROLLMENT_FILE_REJECTS_EMAIL, {
    applicationSource,
    email,
    onSuccess,
  });

export const auditEnrollmentFileRejectsAttachmentDownload = (email:EnrollmentFileRejectsGetEmail) =>
  createAction(EnrollmentFileRejectActionType.AUDIT_ENROLLMENT_FILE_REJECTS_EMAIL_ATTACHMENT_DOWNLOAD, email);

export const changeEnrollmentFileRejectFileProfile = (fileProfileId: FileProfileId) =>
  createAction(EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECT_FILE_PROFILE, fileProfileId);

export const getEnrollmentFileRejectsEmailHistory = (
  id?: string,
  applicationSource?: string,
  pageOption?: Page<EnrollmentFileRejectEmailHistory>,
  filter?: EnrollmentFileRejectEmailHistoryFilters
) => {
  const payload = pipe(
    O.fromNullable(pageOption),
    O.fold(constant(PageFetchAction.Initial({filter})), page =>
      PageFetchAction.Additional({existingPage: page, filter})
    )
  );

  return createAction(
    EnrollmentFileRejectActionType.GET_ENROLLMENT_FILE_REJECTS_EMAIL_HISTORY,
    payload,
    id!,
    applicationSource!
  );
};

export const changeEnrollmentFileRejectsEmailHistory = (
  rejectEmailHistory: AsyncPage<Page<EnrollmentFileRejectEmailHistory>>
) => createAction(EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECTS_EMAIL_HISTORY, rejectEmailHistory);
export const changeEnrollmentFileRejectEmailHistoryFilters = (filters: EnrollmentFileRejectEmailHistoryFilters) =>
  createAction(EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECTS_EMAIL_HISTORY_FILTERS, {
    filters,
  });

export const deleteEnrollmentFileRejectError = (
  caseRequestId: string,
  filter?: EnrollmentFileRejectFilters,
  deleteReason?: number
) =>
  createAction(EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECT_RECORDS, {
    filter,
    caseRequestId,
    deleteReason,
  });
export const changeSort = (sort?: Sort) =>
  createAction(EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECT_SORT, {sort});
export const clearSort = () => createAction(EnrollmentFileRejectActionType.CLEAR_ENROLLMENT_FILE_REJECT_SORT);

export type FileProfileId = Async<string>;

type EnrollmentFileRejectState = {
  policyNumber: string;
  enrollmentFileRejects: AsyncPage<Page<EnrollmentFileReject>>;
  enrollmentFileRejectEmailHistory: AsyncPage<Page<EnrollmentFileRejectEmailHistory>>;
  enrollmentMetrics: Async<OverallEnrollmentMetricsPayload>;
  enrollmentFileRejectDetail: Async<EnrollmentFileRejectDetail>;
  enrollmentFileRejectApplications: AsyncPage<Page<EnrollmentFileRejectApplication>>;
  enrollmentFilesRejectFilter: EnrollmentFileRejectFilters;
  enrollmentFilesRejectDetailsFilter: EnrollmentFileRejectDetailsFilters;
  enrollmentFileRejectEmailHistoryFilter: EnrollmentFileRejectEmailHistoryFilters;
  enrollmentFileRejectNotes: EnrollmentFileNotes[];
  enrollmentRejectFileProfileId: FileProfileId;
  enrollmentFileRejectsEmail: Async<EnrollmentFileRejectsGetEmail>;
  sort: O.Option<Sort>;
};

export const DEFAULT_STATE: EnrollmentFileRejectState = {
  enrollmentFileRejects: new Empty(),
  enrollmentFileRejectDetail: new Empty(),
  enrollmentFileRejectApplications: new Empty(),
  enrollmentFilesRejectFilter: EmptyEnrollmentFileRejectFilter,
  enrollmentFilesRejectDetailsFilter: EmptyEnrollmentFileRejectDetailsFilter,
  enrollmentFileRejectEmailHistory: new Empty(),
  enrollmentFileRejectEmailHistoryFilter: EmptyEnrollmentFileRejectEmailHistoryFilter,
  policyNumber: '',
  enrollmentMetrics: new Empty(),
  enrollmentFileRejectNotes: [],
  enrollmentFileRejectsEmail: new Empty(),
  enrollmentRejectFileProfileId: new Empty(),
  sort: O.none,
};

type EnrollmentFileRejectAction =
  | ReturnType<typeof clearSort>
  | ReturnType<typeof changeSort>
  | ReturnType<typeof changeEnrollmentMetrics>
  | ReturnType<typeof changeEnrollmentFileRejectFilters>
  | ReturnType<typeof changeEnrollmentFileRejectDetailsFilters>
  | ReturnType<typeof changeEnrollmentFileRejectsEmailHistory>
  | ReturnType<typeof changeEnrollmentFileRejectEmailHistoryFilters>
  | ReturnType<typeof getEnrollmentFileRejects>
  | ReturnType<typeof getEnrollmentFileRejectDetailApplications>
  | ReturnType<typeof changeEnrollmentFileRejects>
  | ReturnType<typeof changeEnrollmentFileRejectDetail>
  | ReturnType<typeof changeEnrollmentFileRejectApplication>
  | ReturnType<typeof changeEnrollmentFileRejectNotes>
  | ReturnType<typeof changeEnrollmentFileRejectsEmail>
  | ReturnType<typeof changeEnrollmentFileRejectFileProfile>
  | ReturnType<typeof deleteEnrollmentFileRejectError>;

export default (
  state: EnrollmentFileRejectState = DEFAULT_STATE,
  action: EnrollmentFileRejectAction
): EnrollmentFileRejectState => {
  switch (action.type) {
    case EnrollmentFileRejectActionType.CLEAR_ENROLLMENT_FILE_REJECT_SORT:
      return {
        ...state,
        sort: DEFAULT_STATE.sort,
      };
    case EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECT_SORT:
      return {
        ...state,
        sort: O.fromNullable(action.payload.sort),
      };
    case EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_METRICS:
      return {
        ...state,
        enrollmentMetrics: action.payload,
      };
    case EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECT_DETAIL:
      return {
        ...state,
        enrollmentFileRejectDetail: action.payload,
      };
    case EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECT_DETAIL_APPLICATIONS:
      return {
        ...state,
        enrollmentFileRejectApplications: action.payload,
      };
    case EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECTS_EMAIL_HISTORY:
      return {
        ...state,
        enrollmentFileRejectEmailHistory: action.payload,
      };
    case EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECT_FILTERS:
      return {
        ...state,
        enrollmentFilesRejectFilter: action.payload.filters,
      };
    case EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECT_DETAILS_FILTERS:
      return {
        ...state,
        enrollmentFilesRejectDetailsFilter: action.payload.filters,
      };
    case EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECTS_EMAIL_HISTORY_FILTERS:
      return {
        ...state,
        enrollmentFileRejectEmailHistoryFilter: action.payload.filters,
      };
    case EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECTS:
      return {
        ...state,
        enrollmentFileRejects: action.payload,
      };
    case EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_NOTES:
      return {
        ...state,
        enrollmentFileRejectNotes: action.payload,
      };
    case EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECTS_EMAIL:
      return {
        ...state,
        enrollmentFileRejectsEmail: action.payload,
      };
    case EnrollmentFileRejectActionType.CHANGE_ENROLLMENT_FILE_REJECT_FILE_PROFILE:
      return {
        ...state,
        enrollmentRejectFileProfileId: action.payload,
      };
    default:
      return state;
  }
};
